import * as React from 'react';
import {useTheme} from '@mui/material/styles';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';
import {LineChart, lineElementClasses} from '@mui/x-charts/LineChart';
import {XYVO} from "@/service/analyze";
import {ChartsLabelMark} from "@mui/x-charts";
import {transformMoney} from "@/helper/method";

function AreaGradient({color, id}: { color: string; id: string }) {
    return (
        <defs>
            <linearGradient id={id} x1="0%" y1="0%" x2="0%" y2="90%" gradientUnits="userSpaceOnUse">
                <stop offset="0%" stopColor={color} stopOpacity={1}/>
                <stop offset="100%" stopColor={color} stopOpacity={0.8}/>
            </linearGradient>
        </defs>
    );
}

const colors = ['#4ED7F1', "#FBF3C1"]

export interface RecentYearPaybackProps {
    yearPaybacks: XYVO[]
    loading: boolean
}

export default function RecentYearPayback({yearPaybacks, loading}: RecentYearPaybackProps) {
    return (
        <Card variant="outlined" sx={{width: '100%'}}>
            <CardContent>
                <Stack direction={'row'} alignContent={'center'} alignItems={'center'} sx={{justifyContent: 'space-between'}}>
                    <Typography component="h2" variant="subtitle2" gutterBottom>
                        最近12个月回款
                    </Typography>
                    <Stack
                        direction="row"
                        sx={{
                            alignContent: {xs: 'center', sm: 'flex-start'},
                            alignItems: 'center',
                            gap: 1,
                        }}
                    >
                        <Stack alignItems={'center'} spacing={1} direction={'row'}>
                            <ChartsLabelMark type={'square'} color={colors[0]}/>
                            <Typography sx={{display: 'inline-block', alignItems: 'start'}}>金额</Typography>
                        </Stack>
                        <Stack alignItems={'center'} spacing={1} direction={'row'}>
                            <ChartsLabelMark type={'square'} color={colors[1]}/>
                            <Typography sx={{display: 'inline-block', alignItems: 'start'}}>次数</Typography>
                        </Stack>
                    </Stack>
                </Stack>
                <LineChart
                    hideLegend={true}
                    loading={loading}
                    localeText={{loading: "加载中"}}
                    xAxis={[
                        {
                            scaleType: 'point',
                            data: yearPaybacks?.map(item => item.x),
                            disableLine: true,
                            disableTicks: true,
                        },
                    ]}
                    yAxis={[{
                        id: 'leftAxisId',
                        position: 'left',
                        width: 48,
                        stroke: colors[0],
                        disableLine: true,
                        disableTicks: true,
                        valueFormatter: (value: number) => transformMoney(value),
                    }, {
                        id: 'rightAxisId',
                        position: 'right',
                        width: 40,
                        disableLine: true,
                        disableTicks: true,
                        valueFormatter: (value: number) => `${value}次`,
                    },]}
                    series={[
                        {
                            id: 'money',
                            label: '金额',
                            showMark: false,
                            stack: 'total',
                            yAxisId: 'leftAxisId',
                            area: true,
                            stackOrder: 'ascending',
                            data: yearPaybacks?.map(item => item.y1),
                            valueFormatter: value => transformMoney(value)
                        },
                        {
                            id: 'amount',
                            label: '次数',
                            yAxisId: 'rightAxisId',
                            showMark: false,
                            stack: 'total',
                            area: true,
                            stackOrder: 'ascending',
                            data: yearPaybacks?.map(item => item.y2),
                        },
                    ]}
                    height={240}
                    margin={{left: 0, right: 0, top: 20, bottom: 10}}
                    // grid={{horizontal: true}}
                    sx={{
                        [`& .${lineElementClasses.root}`]: {
                            // display: 'none',
                            stroke: 'none',
                        },
                        '& .MuiAreaElement-series-money': {
                            fill: "url('#money')",
                        },
                        '& .MuiAreaElement-series-amount': {
                            fill: "url('#amount')",
                        },
                    }}
                >
                    <AreaGradient color={colors[0]} id="amount"/>
                    <AreaGradient color={colors[1]} id="money"/>
                </LineChart>
            </CardContent>
        </Card>
    );
}
